import { fetchAuthSessionWithLoginMismatchRetry } from "../../utils/amplifyAuthRecovery";
import { DONATIONS_API_URL, DONATIONS_ENABLED } from "../donations/config";
import { normalizeEin } from "../donations/fundraiserIntent";
import { evtLiveOwnerHeaders } from "../teams/workspace";
import { t } from "@/localization";

const REQUEST_TIMEOUT_MS = 30000;
// The nonprofit waiver claim runs before event creation; keep it short.
const CLAIM_TIMEOUT_MS = 10000;

const STATUS_MESSAGES = {
  401: t("Please sign in again."),
  403: t("You don't have access to fundraising for this event."),
  404: t("Fundraising isn't available yet. Please try again later."),
  409: t("Another fundraiser update is being saved. Please try again."),
  429: t("Too many requests. Please wait a moment and try again."),
};
const UNAVAILABLE_MESSAGE = t(
  "Fundraising is temporarily unavailable. Please try again later."
);
const OFFLINE_MESSAGE = t(
  "Could not reach fundraising. Check your internet connection and try again."
);

export class DonationRequestError extends Error {
  constructor(message, status = 0) {
    super(message);
    this.name = "DonationRequestError";
    this.status = status;
  }
}

// Show the API's own short, plain-text errors ("Choose an event.", "Select up
// to 20 organizations."). HTML error pages, stack traces or anything long get a
// friendly message instead.
const apiErrorMessage = (data) => {
  const message =
    data && typeof data.error === "string" ? data.error.trim() : "";
  return message && message.length <= 200 && !/[<>{}]/.test(message)
    ? message
    : "";
};

const getAccessToken = async () => {
  try {
    const session = await fetchAuthSessionWithLoginMismatchRetry();
    return session?.tokens?.accessToken?.toString() || null;
  } catch (error) {
    return null;
  }
};

async function authorizedRequest(
  url,
  body,
  timeoutMs = REQUEST_TIMEOUT_MS,
  extraHeaders = {}
) {
  const token = await getAccessToken();
  if (!token) throw new DonationRequestError(STATUS_MESSAGES[401], 401);

  const controller =
    typeof AbortController !== "undefined" ? new AbortController() : null;
  const timeout = controller
    ? setTimeout(() => controller.abort(), timeoutMs)
    : null;

  let response;
  try {
    response = await fetch(url, {
      method: body ? "POST" : "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        ...extraHeaders,
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
      ...(controller ? { signal: controller.signal } : {}),
    });
  } catch (error) {
    throw new DonationRequestError(OFFLINE_MESSAGE, 0);
  } finally {
    if (timeout) clearTimeout(timeout);
  }

  let data = null;
  try {
    const text = await response.text();
    data = text ? JSON.parse(text) : null;
  } catch (error) {
    // Non-JSON responses (e.g. an HTML 404 page) fall through to the checks below.
    data = null;
  }

  if (!response.ok) {
    throw new DonationRequestError(
      apiErrorMessage(data) ||
        STATUS_MESSAGES[response.status] ||
        UNAVAILABLE_MESSAGE,
      response.status
    );
  }
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    throw new DonationRequestError(UNAVAILABLE_MESSAGE, response.status);
  }
  return data;
}

// The event's slug, from the body or the query string (organizations has none).
const slugOf = (path, body) => {
  if (typeof body?.slug === "string") return body.slug;
  const match = /[?&]slug=([^&]*)/.exec(path);
  return match ? decodeURIComponent(match[1]) : undefined;
};

// In someone else's account (Teams), evt.live acts for its owner: admins set
// up fundraising, editors' title changes keep the fundraiser's name in step.
export async function donationRequest(path, body) {
  return authorizedRequest(
    `${DONATIONS_API_URL}/api/pledge/${path}`,
    body,
    REQUEST_TIMEOUT_MS,
    evtLiveOwnerHeaders(slugOf(path, body))
  );
}

// Links the eventlive.pro/nonprofit lead to the signed-in broadcaster's own
// account (evt.live POST /api/nonprofit/claim; never the account worked in): { status: "granted", until } |
// { status: "pending_verification" } | { status: "not_eligible" } | ...
// Errors carry the HTTP status (0 = network).
export async function claimNonprofitLead(leadId) {
  return authorizedRequest(
    `${DONATIONS_API_URL}/api/nonprofit/claim`,
    { lead_id: leadId },
    CLAIM_TIMEOUT_MS
  );
}

const organizationsFrom = (data) =>
  Array.isArray(data?.organizations)
    ? data.organizations
    : data?.organization
    ? [data.organization]
    : [];

export async function searchOrganizations(query) {
  const data = await donationRequest(
    `organizations?q=${encodeURIComponent(query.trim())}`
  );
  return organizationsFrom(data).filter((org) => org?.id);
}

export async function fetchOrganization(id) {
  const data = await donationRequest(
    `organizations?id=${encodeURIComponent(id)}`
  );
  return organizationsFrom(data).find((org) => org?.id === id) || null;
}

const isAuthError = (error) => error?.status === 401 || error?.status === 403;

// Finds the organization a nonprofit picked on eventlive.pro/nonprofit.
// Only an exact Pledge id (or EIN, when no id was given) match is returned, so a
// similarly named charity is never preselected.
export async function resolvePledgeOrganization({ id, ein, name } = {}) {
  const einValue = normalizeEin(ein);
  if (id) {
    try {
      const org = await fetchOrganization(id);
      if (org) return org;
    } catch (error) {
      if (isAuthError(error)) throw error;
      // Older API without id lookup: fall back to search below.
    }
  }
  if (!id && !einValue) return null;

  const queries = [einValue, name]
    .map((q) => (typeof q === "string" ? q.trim() : ""))
    .filter((q, i, all) => q.length >= 2 && all.indexOf(q) === i);
  for (const query of queries) {
    let results = [];
    try {
      results = await searchOrganizations(query);
    } catch (error) {
      if (isAuthError(error)) throw error;
      continue;
    }
    const match = results.find((org) =>
      id ? org.id === id : normalizeEin(org.ein) === einValue
    );
    if (match) return match;
  }
  return null;
}

// Keeps the Pledge fundraiser name in step with the event title. It runs in
// the background after the details autosave has finished: saves never wait for
// it and its errors are never shown on the details or link forms.
export const DONATION_SYNC_DELAY_MS = 5000;
const DONATION_SYNC_RETRY_MS = 20000;
const DONATION_SYNC_MAX_RETRIES = 2;
const pendingSyncs = new Map();

const shouldRetrySync = (error) =>
  error?.status === 0 || error?.status === 409 || error?.status >= 500;

async function runDonationSync(slug) {
  const entry = pendingSyncs.get(slug);
  if (!entry) return;
  entry.timer = null;
  entry.running = true;
  entry.again = false;
  let retry = false;
  try {
    await donationRequest("settings", { slug, sync_only: true });
    entry.retries = 0;
  } catch (error) {
    retry = shouldRetrySync(error) && entry.retries < DONATION_SYNC_MAX_RETRIES;
    entry.retries = retry ? entry.retries + 1 : 0;
    console.warn("Fundraiser name sync skipped:", error?.message);
  } finally {
    entry.running = false;
  }
  if (entry.again || retry) {
    entry.timer = setTimeout(
      () => runDonationSync(slug),
      entry.again ? DONATION_SYNC_DELAY_MS : DONATION_SYNC_RETRY_MS
    );
  } else {
    pendingSyncs.delete(slug);
  }
}

export function scheduleDonationSync(event) {
  const slug = event?.slug;
  if (!DONATIONS_ENABLED || !slug || !event?.donation_fundraiser_id) {
    return false;
  }
  const entry = pendingSyncs.get(slug) || {
    timer: null,
    running: false,
    again: false,
    retries: 0,
  };
  pendingSyncs.set(slug, entry);
  if (entry.running) {
    entry.again = true;
    return true;
  }
  if (entry.timer) clearTimeout(entry.timer);
  entry.retries = 0;
  entry.timer = setTimeout(() => runDonationSync(slug), DONATION_SYNC_DELAY_MS);
  return true;
}
